import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Calendar, Copy, RefreshCw } from "lucide-react";
import { getCalendarFeedToken, rotateCalendarFeedToken } from "@/lib/admin-secrets.functions";
import { submitSitemapToGSC } from "@/lib/seo.functions";

export const Route = createFileRoute("/admin/settings")({ component: AdminSettings });

function AdminSettings() {
  const [s, setS] = useState<any>(null);
  const [feedToken, setFeedToken] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fetchToken = useServerFn(getCalendarFeedToken);

  useEffect(() => {
    supabase.from("settings").select("*").eq("id", 1).maybeSingle().then(({ data }) => setS(data));
    fetchToken().then((res) => setFeedToken(res.token)).catch(() => setFeedToken(null));
  }, [fetchToken]);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  const set = (k: string, v: any) => setS((p: any) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("settings").update({
      tax_rate: Number(s.tax_rate), cleaning_fee: Number(s.cleaning_fee), late_fee_per_hour: Number(s.late_fee_per_hour),
      business_name: s.business_name, contact_email: s.contact_email, contact_phone: s.contact_phone,
      business_hours: s.business_hours, policies: s.policies,
      trips_count: s.trips_count ? Number(s.trips_count) : null,
      avg_rating: s.avg_rating ? Number(s.avg_rating) : null,
      years_in_business: s.years_in_business ? Number(s.years_in_business) : null,
      founder_name: s.founder_name || null, founder_role: s.founder_role || null,
      founder_bio: s.founder_bio || null, founder_photo_url: s.founder_photo_url || null,
      service_areas: s.service_areas || null,
      ga4_measurement_id: s.ga4_measurement_id?.trim() || null,
      gsc_verification: s.gsc_verification?.trim() || null,
      bing_verification: s.bing_verification?.trim() || null,
      clarity_project_id: s.clarity_project_id?.trim() || null,
      robots_sitemap_enabled: s.robots_sitemap_enabled !== false,
    }).eq("id", 1);
    setSaving(false);
    if (error) toast.error(error.message); else toast.success("Saved");
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl text-espresso">Settings</h1>
      <Card className="mt-6 space-y-4 p-6">
        <h2 className="font-display text-lg text-espresso">Business</h2>
        <F label="Business name"><Input value={s.business_name || ""} onChange={e => set("business_name", e.target.value)} /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Contact email"><Input type="email" value={s.contact_email || ""} onChange={e => set("contact_email", e.target.value)} /></F>
          <F label="Contact phone"><Input value={s.contact_phone || ""} onChange={e => set("contact_phone", e.target.value)} /></F>
        </div>
        <F label="Business hours (shown on Contact page, FAQ, and vehicle pages)">
          <Textarea rows={3} value={s.business_hours || ""} onChange={e => set("business_hours", e.target.value)} placeholder={"Mon–Sat: 9:00 AM – 7:00 PM\nSun: 10:00 AM – 5:00 PM"} />
        </F>
        <div className="grid grid-cols-3 gap-3">
          <F label="Tax rate (%)"><Input type="number" step="0.01" value={s.tax_rate} onChange={e => set("tax_rate", e.target.value)} /></F>
          <F label="Cleaning fee ($)"><Input type="number" step="0.01" value={s.cleaning_fee} onChange={e => set("cleaning_fee", e.target.value)} /></F>
          <F label="Late fee ($/hour)"><Input type="number" step="0.01" value={s.late_fee_per_hour} onChange={e => set("late_fee_per_hour", e.target.value)} /></F>
        </div>
        <F label="Policies (shown to customers)"><Textarea rows={6} value={s.policies || ""} onChange={e => set("policies", e.target.value)} /></F>

        <div className="pt-4 border-t border-border" />
        <h2 className="font-display text-lg text-espresso">About page · Stats strip</h2>
        <p className="text-sm text-muted-foreground -mt-2">Leave blank to hide. Shown on the About page hero.</p>
        <div className="grid grid-cols-3 gap-3">
          <F label="Trips completed"><Input type="number" value={s.trips_count ?? ""} onChange={e => set("trips_count", e.target.value)} placeholder="500" /></F>
          <F label="Avg rating (0–5)"><Input type="number" step="0.1" min="0" max="5" value={s.avg_rating ?? ""} onChange={e => set("avg_rating", e.target.value)} placeholder="4.9" /></F>
          <F label="Years in Miami"><Input type="number" value={s.years_in_business ?? ""} onChange={e => set("years_in_business", e.target.value)} placeholder="5" /></F>
        </div>

        <div className="pt-4 border-t border-border" />
        <h2 className="font-display text-lg text-espresso">About page · Founder</h2>
        <p className="text-sm text-muted-foreground -mt-2">Leave name blank to hide the entire founder section.</p>
        <div className="grid grid-cols-2 gap-3">
          <F label="Founder name"><Input value={s.founder_name || ""} onChange={e => set("founder_name", e.target.value)} placeholder="Alex Creed" /></F>
          <F label="Role / title"><Input value={s.founder_role || ""} onChange={e => set("founder_role", e.target.value)} placeholder="Owner & Founder" /></F>
        </div>
        <F label="Photo URL (optional)"><Input value={s.founder_photo_url || ""} onChange={e => set("founder_photo_url", e.target.value)} placeholder="https://…" /></F>
        <F label="Short bio"><Textarea rows={4} value={s.founder_bio || ""} onChange={e => set("founder_bio", e.target.value)} placeholder="Born and raised in Miami…" /></F>

        <div className="pt-4 border-t border-border" />
        <h2 className="font-display text-lg text-espresso">About page · Service areas</h2>
        <F label="One area per line (leave blank to hide section)">
          <Textarea rows={6} value={s.service_areas || ""} onChange={e => set("service_areas", e.target.value)} placeholder={"Miami International Airport (MIA)\nSouth Beach\nBrickell\nWynwood\nFort Lauderdale (FLL)"} />
        </F>

        <div className="pt-4 border-t border-border" />
        <h2 className="font-display text-lg text-espresso">Analytics & search verification</h2>
        <p className="text-sm text-muted-foreground -mt-2">
          Paste IDs/tokens from each provider. Leave blank to disable. Tags are injected site-wide automatically.
        </p>
        <F label="Google Analytics 4 — Measurement ID">
          <Input value={s.ga4_measurement_id || ""} onChange={e => set("ga4_measurement_id", e.target.value)} placeholder="G-XXXXXXXXXX" />
        </F>
        <F label="Google Search Console — verification token (content of the meta tag only)">
          <Input value={s.gsc_verification || ""} onChange={e => set("gsc_verification", e.target.value)} placeholder="abc123…" />
        </F>
        <F label="Bing Webmaster — verification token (content of msvalidate.01 meta only)">
          <Input value={s.bing_verification || ""} onChange={e => set("bing_verification", e.target.value)} placeholder="ABCDEF1234567890…" />
        </F>
        <F label="Microsoft Clarity — Project ID">
          <Input value={s.clarity_project_id || ""} onChange={e => set("clarity_project_id", e.target.value)} placeholder="abcd1234xy" />
        </F>

        <div className="pt-4 border-t border-border" />
        <h2 className="font-display text-lg text-espresso">Sitemap submission</h2>
        <p className="text-sm text-muted-foreground -mt-2">
          Regenerate <code>/sitemap.xml</code> and resubmit it to Google Search Console so indexing picks up new pages faster. Run this after publishing.
        </p>
        <SitemapSubmitRow />

        <div className="flex items-start gap-3 rounded-md border border-border p-3">
          <input
            id="robots-sitemap-toggle"
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={s.robots_sitemap_enabled !== false}
            onChange={(e) => set("robots_sitemap_enabled", e.target.checked)}
          />
          <div className="space-y-0.5">
            <Label htmlFor="robots-sitemap-toggle" className="cursor-pointer">
              Reference sitemap in <code>robots.txt</code>
            </Label>
            <p className="text-xs text-muted-foreground">
              When on, <code>/robots.txt</code> includes a <code>Sitemap:</code> line pointing at <code>/sitemap.xml</code> so crawlers discover it automatically. Turn off to hide it.
            </p>
          </div>
        </div>




        <div className="flex justify-end pt-2"><Button onClick={save} disabled={saving} className="bg-primary">Save settings</Button></div>
      </Card>

      <CalendarFeedCard token={feedToken} onRotated={(t) => setFeedToken(t)} />
    </div>
  );
}

function SitemapSubmitRow() {
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState<{ lastSubmitted: string | null; warnings: number | null; errors: number | null } | null>(null);
  const submit = useServerFn(submitSitemapToGSC);

  const run = async () => {
    setBusy(true);
    try {
      const res = await submit();
      setLastResult({ lastSubmitted: res.lastSubmitted, warnings: res.warnings, errors: res.errors });
      toast.success("Sitemap submitted to Google Search Console");
    } catch (e: any) {
      toast.error(e?.message || "Failed to submit sitemap");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button onClick={run} disabled={busy} variant="outline">
        {busy ? <RefreshCw className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
        Regenerate & submit sitemap
      </Button>
      {lastResult && (
        <p className="text-xs text-muted-foreground">
          Last submitted: {lastResult.lastSubmitted ? new Date(lastResult.lastSubmitted).toLocaleString() : "just now"}
          {lastResult.errors != null && ` · ${lastResult.errors} errors`}
          {lastResult.warnings != null && ` · ${lastResult.warnings} warnings`}
        </p>
      )}
    </div>
  );
}

function CalendarFeedCard({ token, onRotated }: { token: string | null; onRotated: (t: string) => void }) {
  const [rotating, setRotating] = useState(false);
  const rotateFn = useServerFn(rotateCalendarFeedToken);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = token ? `${origin}/api/public/calendar/${token}/feed.ics` : "";
  const webcalUrl = url.replace(/^https?:/, "webcal:");

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };

  const rotate = async () => {
    if (!confirm("Generate a new feed URL? Anyone using the old URL will stop receiving updates.")) return;
    setRotating(true);
    try {
      const res = await rotateFn();
      onRotated(res.token);
      toast.success("New calendar URL generated");
    } catch (e: any) {
      toast.error(e?.message || "Failed to rotate token");
    } finally {
      setRotating(false);
    }
  };

  return (
    <Card className="mt-6 space-y-4 p-6">
      <div className="flex items-center gap-2">
        <Calendar className="h-5 w-5 text-accent" />
        <h2 className="font-display text-lg text-espresso">Calendar subscription</h2>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">
        Subscribe to all pending & approved bookings from Google Calendar, Apple Calendar, or Outlook. Updates automatically every few minutes.
      </p>
      {token ? (
        <>
          <F label="Subscribe URL (one-click for Apple Calendar / Outlook)">
            <div className="flex gap-2">
              <Input readOnly value={webcalUrl} onFocus={(e) => e.currentTarget.select()} />
              <Button variant="outline" size="icon" onClick={() => copy(webcalUrl)} title="Copy"><Copy className="h-4 w-4" /></Button>
            </div>
          </F>
          <F label="HTTPS URL (paste into Google Calendar → ‘From URL’)">
            <div className="flex gap-2">
              <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
              <Button variant="outline" size="icon" onClick={() => copy(url)} title="Copy"><Copy className="h-4 w-4" /></Button>
            </div>
          </F>
          <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
            <div><strong>Google Calendar:</strong> Other calendars → + → From URL → paste the HTTPS URL.</div>
            <div><strong>Apple Calendar:</strong> File → New Calendar Subscription → paste the webcal URL.</div>
            <div><strong>Outlook:</strong> Add calendar → Subscribe from web → paste the HTTPS URL.</div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={rotate} disabled={rotating}>
              {rotating ? <RefreshCw className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
              Regenerate URL
            </Button>
          </div>
        </>
      ) : (
        <Button onClick={rotate} disabled={rotating}>Generate calendar URL</Button>
      )}
    </Card>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><Label>{label}</Label>{children}</div>;
}
